// ============================================================================
// components/remote/RemoteHostPanel.tsx — ХОСТ «Удалённого доступа»:
//   • Android/Capacitor — экран телефона через нативный плагин (LiveKit или
//     наш контракт v2 с прослушиванием порта в локальной сети);
//   • браузер — показ экрана ЧЕРЕЗ РЕЛЕЙ сервера (исходящее соединение,
//     NAT/входящие порты не нужны — useBrowserContractHost).
// Управление включается только решением человека (нативный хост); браузерный
// показ управление не выполняет — честный отказ.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { Accessibility, Copy, Loader2, Monitor, Network, Play, ShieldCheck, ShieldOff, Square } from 'lucide-react';
import RemoteHost, {
  isRemoteHostSupported,
  type RemoteHostStatus,
  type RemoteHostTransport,
} from '@/lib/remote/remoteHost';
import { RemoteTransportSelector, remoteTransportLabel } from '@/components/remote/RemoteTransportSelector';
import { useBrowserContractHost } from '@/components/remote/useBrowserContractHost';
import { getSDK } from '@/store/sdkStore';
import { eventBusHub } from '@/lib/eventBusHub';
import {
  newRemoteSessionId,
  publishRemoteSignal,
  remoteRoomName,
  REMOTE_QUALITY_PRESETS,
} from '@/lib/remote/remoteSession';
import { logEvent } from '@/lib/eventLog';
import { copyToClipboard } from '@/lib/clipboard';
import { registerRemoteEndpointOnPlatform } from '@/lib/remote/contractEndpoint';

const EMPTY_STATUS: RemoteHostStatus = {
  running: false,
  sharing: false,
  controlOn: false,
  controlRequested: false,
  sessionId: '',
  room: '',
  transport: '',
  quality: '',
  endpoint: '',
  error: '',
  viewers: 0,
};

const CONTRACT_DEFAULT_PORT = 8765;

/** Тексты фаз браузерного показа через релей (статус человеку). */
const RELAY_PHASE_TEXT: Record<string, string> = {
  capturing: 'запрос доступа к экрану…',
  connecting: 'подключение к серверу…',
  waiting: 'ждём вторую сторону',
  paired: 'соединено с зрителем',
  streaming: 'идёт показ',
  stopped: 'остановлен',
  error: 'ошибка',
};

export function RemoteHostPanel() {
  const nativeSupported = isRemoteHostSupported();
  const browserHost = useBrowserContractHost();
  const supported = nativeSupported || browserHost.supported;
  const [status, setStatus] = useState<RemoteHostStatus>(EMPTY_STATUS);
  const [sessionId, setSessionId] = useState(() => newRemoteSessionId('u'));
  const [transport, setTransport] = useState<RemoteHostTransport>(() => (isRemoteHostSupported() ? 'livekit' : 'relay'));
  const [quality, setQuality] = useState('auto');
  const [port, setPort] = useState(String(CONTRACT_DEFAULT_PORT));
  const [accessibility, setAccessibility] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [incoming, setIncoming] = useState('');
  const [copied, setCopied] = useState(false);
  const listenerRef = useRef<{ remove: () => Promise<void> } | null>(null);
  const announcedRef = useRef('');

  const allowedTransports: RemoteHostTransport[] = nativeSupported
    ? (browserHost.supported ? ['livekit', 'contract', 'relay'] : ['livekit', 'contract'])
    : ['relay'];
  const relayActive = ['capturing', 'connecting', 'waiting', 'paired', 'streaming'].includes(browserHost.phase || '');
  const running = transport === 'relay' ? relayActive : status.running;

  const log = useCallback((level: 'info' | 'error', message: string, details?: string) => {
    void logEvent({ level, category: 'ui', message, entityType: 'remote', entityCode: 'host', details: details || '' });
  }, []);

  const refresh = useCallback(async () => {
    if (!nativeSupported) return;
    try {
      setStatus(await RemoteHost.isRunning());
    } catch (e) {
      log('error', 'удалённый доступ (хост): не удалось получить состояние', String((e as Error)?.message || e));
    }
  }, [nativeSupported, log]);

  const refreshAccessibility = useCallback(async () => {
    if (!nativeSupported) return;
    try {
      const r = await RemoteHost.isAccessibilityEnabled();
      setAccessibility(r.enabled);
    } catch (e) {
      log('error', 'удалённый доступ (хост): не удалось проверить спец. возможности', String((e as Error)?.message || e));
    }
  }, [nativeSupported, log]);

  useEffect(() => {
    if (!nativeSupported) return;
    void refresh();
    void refreshAccessibility();
    let disposed = false;
    RemoteHost.addListener('status', (s) => {
      if (!disposed) setStatus(s);
    })
      .then((h) => {
        listenerRef.current = h;
      })
      .catch((e: unknown) => log('error', 'удалённый доступ (хост): не удалось подписаться на состояние', String((e as Error)?.message || e)));
    const unsub = eventBusHub.observe((ev) => {
      const m = /^remote\.session\.started\.(.+)$/.exec(ev.topic);
      if (m && m[1]) {
        setIncoming(m[1]);
        setSessionId(m[1]);
      }
    }, 'remote.session.started.');
    return () => {
      disposed = true;
      void listenerRef.current?.remove();
      listenerRef.current = null;
      unsub();
    };
  }, [supported, refresh, refreshAccessibility, log]);

  // Контрактный хост — СЕРВЕР: адрес РЕГИСТРИРУЕТСЯ на ПЛАТФОРМЕ (SSoT —
  // зритель резолвит его по sessionId, ручного ввода адреса нет) + публикуем
  // сигнал обнаружения сеанса по шине (один раз на адрес).
  useEffect(() => {
    if (!status.running || status.transport !== 'contract' || !status.endpoint) return;
    if (announcedRef.current === status.endpoint) return;
    announcedRef.current = status.endpoint;
    const sid = status.sessionId || sessionId;
    registerRemoteEndpointOnPlatform(sid, status.endpoint)
      .then(() => log('info', 'удалённый доступ (хост): адрес сеанса зарегистрирован на платформе', status.endpoint))
      .catch((e: unknown) => {
        const msg = String((e as Error)?.message || e);
        setError(`Не удалось зарегистрировать адрес сеанса: ${msg}`);
        log('error', 'удалённый доступ (хост): регистрация адреса не удалась', msg);
        // Повторная попытка — на следующем обновлении состояния (без таймеров).
        announcedRef.current = '';
      });
    publishRemoteSignal(`remote.session.endpoint.${sid}`, {
      url: status.endpoint,
      transport: 'contract',
    });
  }, [status.running, status.transport, status.endpoint, status.sessionId, sessionId, log]);

  const start = useCallback(async () => {
    setError('');
    if (transport === 'relay') {
      await browserHost.start(sessionId, quality);
      return;
    }
    setBusy(true);
    try {
      if (!nativeSupported) {
        throw new Error('Нативный хост доступен только в Android-приложении — выберите «Через сервер (релей)»');
      }
      if (!accessibility) {
        await RemoteHost.openAccessibilitySettings();
        throw new Error('Включите «HydraDatacentre: удалённое управление» и вернитесь, затем нажмите ещё раз');
      }
      const sid = sessionId.trim();
      if (!sid) throw new Error('Укажите идентификатор сеанса');
      const room = remoteRoomName(sid);

      if (transport === 'livekit') {
        const creds: { url?: string; token?: string } = await getSDK().media.createLiveKitToken({ room } as never);
        const url = String(creds?.url || '');
        const token = String(creds?.token || '');
        if (!url || !token) throw new Error('Удалённый доступ недоступен (LiveKit не настроен)');
        await RemoteHost.startRemoteHost({ transport: 'livekit', url, token, room, sessionId: sid });
      } else {
        const parsedPort = Number.parseInt(port.trim(), 10);
        if (!Number.isInteger(parsedPort) || parsedPort < 1 || parsedPort > 65535) {
          throw new Error('Порт должен быть целым числом 1…65535');
        }
        await RemoteHost.startRemoteHost({ transport: 'contract', room, sessionId: sid, quality, port: parsedPort });
      }
      log('info', 'удалённый доступ (хост): показ экрана запрошен', JSON.stringify({ room, transport, quality }));
      await refresh();
    } catch (e) {
      const msg = String((e as Error)?.message || e);
      setError(msg);
      log('error', 'удалённый доступ (хост): не удалось начать показ', msg);
    } finally {
      setBusy(false);
    }
  }, [accessibility, nativeSupported, sessionId, transport, quality, port, refresh, log, browserHost]);

  const stop = useCallback(async () => {
    setError('');
    if (transport === 'relay') {
      await browserHost.stop();
      return;
    }
    setBusy(true);
    try {
      await RemoteHost.stopRemoteHost();
      announcedRef.current = '';
      await refresh();
      log('info', 'удалённый доступ (хост): показ остановлен');
    } catch (e) {
      const msg = String((e as Error)?.message || e);
      setError(msg);
      log('error', 'удалённый доступ (хост): не удалось остановить показ', msg);
    } finally {
      setBusy(false);
    }
  }, [transport, browserHost, refresh, log]);

  const decideControl = useCallback(
    async (allow: boolean) => {
      try {
        if (allow) await RemoteHost.allowControl();
        else await RemoteHost.denyControl();
        await refresh();
      } catch (e) {
        log('error', 'удалённый доступ (хост): не удалось изменить режим управления', String((e as Error)?.message || e));
      }
    },
    [refresh, log],
  );

  const enableAccessibility = useCallback(async () => {
    try {
      await RemoteHost.openAccessibilitySettings();
    } catch (e) {
      log('error', 'удалённый доступ (хост): не удалось открыть настройки', String((e as Error)?.message || e));
    }
  }, [log]);

  const copyEndpoint = useCallback(async (value: string) => {
    try {
      await copyToClipboard(value);
      setCopied(true);
    } catch (e) {
      log('error', 'удалённый доступ (хост): не удалось скопировать адрес', String((e as Error)?.message || e));
    }
  }, [log]);

  if (!supported) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-xs italic text-muted-foreground">
        Показ экрана недоступен: в Android-приложении — нативный хост, в браузере — захват экрана + H.264 (WebCodecs).
      </div>
    );
  }

  const activeTransportLabel = remoteTransportLabel(
    transport === 'relay' ? 'relay' : (status.transport || transport),
  );
  const anyBusy = busy || browserHost.busy;
  const anyError = error || (transport === 'relay' ? browserHost.error : status.error);

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-4">
      <div className="flex items-center gap-2">
        <Monitor className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">{nativeSupported ? 'Показ экрана телефона' : 'Показ экрана через сервер'}</span>
        <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] ${running ? 'bg-green-600/20 text-green-500' : 'bg-muted text-muted-foreground'}`}>
          {running
            ? (transport === 'relay'
              ? (RELAY_PHASE_TEXT[browserHost.phase || ''] || 'работает')
              : (status.sharing ? 'идёт показ' : 'подключение…'))
            : 'остановлен'}
        </span>
      </div>

      {incoming && !running && (
        <div className="rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-[11px]">
          Запрос показа экрана: сеанс <span className="font-mono">{incoming}</span>
        </div>
      )}

      {nativeSupported && (
        <button
          type="button"
          onClick={enableAccessibility}
          className={`flex items-center gap-2 rounded-md border px-3 py-2 text-left text-xs ${accessibility ? 'border-green-600/40 bg-green-600/10' : 'border-amber-500/40 bg-amber-500/10'}`}
        >
          <Accessibility className={`h-4 w-4 ${accessibility ? 'text-green-500' : 'text-amber-500'}`} />
          <span className="flex-1">
            Служба спец. возможностей: {accessibility ? 'включена' : 'выключена'}
            <span className="block text-[10px] text-muted-foreground">
              Нужна для приёма касаний/свайпов/текста. {accessibility ? '' : 'Нажмите, чтобы открыть настройки.'}
            </span>
          </span>
        </button>
      )}

      <RemoteTransportSelector value={transport} disabled={running} allowed={allowedTransports} onChange={setTransport} />

      <label className="flex flex-col gap-1 text-xs">
        Идентификатор сеанса{transport === 'livekit' ? ' (room = remote_{sessionId})' : ''}
        <input
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          disabled={running}
          className="rounded-md border bg-background px-2 py-1.5 font-mono text-xs outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
        />
      </label>

      {transport !== 'livekit' && (
        <div className="flex flex-wrap gap-3">
          <label className="flex flex-col gap-1 text-xs">
            Качество
            <select
              value={quality}
              onChange={(e) => setQuality(e.target.value)}
              disabled={running}
              className="rounded-md border bg-background px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
            >
              {REMOTE_QUALITY_PRESETS.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          {transport === 'contract' && (
            <label className="flex flex-col gap-1 text-xs">
              Порт прослушивания
              <input
                value={port}
                onChange={(e) => setPort(e.target.value)}
                disabled={running}
                inputMode="numeric"
                className="w-28 rounded-md border bg-background px-2 py-1.5 font-mono text-xs outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
              />
            </label>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!running ? (
          <button
            type="button"
            onClick={() => void start()}
            disabled={anyBusy}
            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-60"
          >
            {anyBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Показать экран
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void stop()}
            disabled={anyBusy}
            className="flex items-center gap-1.5 rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground disabled:opacity-60"
          >
            {anyBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />}
            Остановить
          </button>
        )}
      </div>

      {running && transport === 'relay' && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
          Транспорт: {activeTransportLabel} · Статус: {RELAY_PHASE_TEXT[browserHost.phase || ''] || 'работает'}
          {' · '}
          {browserHost.stats.fps}fps · {(browserHost.stats.kbps / 1000).toFixed(1)} Мбит/с
          {' · '}
          Управление недоступно (браузерный показ)
        </div>
      )}

      {running && transport !== 'relay' && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
          Транспорт: {activeTransportLabel}
          {' · '}
          Зрителей: {status.viewers}
          {' · '}
          Управление: {status.controlOn ? 'разрешено' : status.controlRequested ? 'запрошено' : 'выключено'}
        </div>
      )}

      {transport === 'relay' && browserHost.endpoint && (running || browserHost.error) && (
        <div className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-[11px]">
          <Network className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 break-all font-mono">{browserHost.endpoint}</span>
          <button
            type="button"
            onClick={() => void copyEndpoint(browserHost.endpoint)}
            className="flex shrink-0 items-center gap-1 rounded border px-2 py-1 text-[11px]"
          >
            <Copy className="h-3 w-3" />
            {copied ? 'Скопировано' : 'Копировать'}
          </button>
        </div>
      )}

      {running && transport === 'contract' && status.endpoint && (
        <div className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-[11px]">
          <Network className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 break-all font-mono">{status.endpoint}</span>
          <button
            type="button"
            onClick={() => void copyEndpoint(status.endpoint)}
            className="flex shrink-0 items-center gap-1 rounded border px-2 py-1 text-[11px]"
          >
            <Copy className="h-3 w-3" />
            {copied ? 'Скопировано' : 'Копировать'}
          </button>
        </div>
      )}

      {transport !== 'relay' && status.controlRequested && !status.controlOn && (
        <div className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px]">
          <ShieldCheck className="h-4 w-4 text-amber-500" />
          <span className="flex-1">Зритель запрашивает управление вашим телефоном</span>
          <button type="button" onClick={() => void decideControl(true)} className="rounded bg-primary px-2 py-1 text-[11px] text-primary-foreground">
            Разрешить
          </button>
          <button type="button" onClick={() => void decideControl(false)} className="rounded border px-2 py-1 text-[11px]">
            Запретить
          </button>
        </div>
      )}

      {transport !== 'relay' && status.controlOn && (
        <button
          type="button"
          onClick={() => void decideControl(false)}
          className="flex items-center gap-2 rounded-md border px-3 py-2 text-[11px]"
        >
          <ShieldOff className="h-4 w-4 text-destructive" />
          Остановить управление
        </button>
      )}

      {transport === 'relay' && browserHost.notice && !anyError && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">{browserHost.notice}</div>
      )}

      {anyError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[11px] text-destructive">
          {anyError}
        </div>
      )}
    </div>
  );
}
